// @flow
import ArchiverClientApi from "../API/archiverClientApi";
import Popup from "../GUI_COMMON/COMPONENTS/Popup";
import ErrorPopup from "../GUI_COMMON/COMPONENTS/ErrorPopup";
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import React from "react";
import type {BTOBox, BTOMagazineItem} from "../COMPONENTS/BTOAddUpdatePage";
import type {DepartmentWithLocation} from "../GUI_COMMON/SUPPORT/MagazineV2";
import {SampleBadgePicked} from "../COMPONENTS/SampleBadgePicked";
import {SampleBadgeDifferentOrder} from "../COMPONENTS/SampleBadgeDifferentOrder";
import {SampleBadgeDuplicate} from "../COMPONENTS/SampleBadgeDuplicate";
import {SampleBadgeDeleted} from "../COMPONENTS/SampleBadgeDeleted";
import {SampleBadgeOffPremises} from "../COMPONENTS/SampleBadgeOffPremises";
import {SampleBadgeOut} from "../COMPONENTS/SampleBadgeOut";
import {SampleBadgeIn} from "../COMPONENTS/SampleBadgeIn";

export const DEFAULT_LOCATION_NAME: string = "Default Location";

class ArchiverGuiUtils {

    /**
     * get and dump out sample item information related to a sample that is marked as OUT.
     * This is used in several pages, so we share.
     */
    static GetSampleOutInfo(item) {
        ArchiverClientApi.getSampleLastRemovalDetails(item.id)
        .then(({lastPermRemoval, lastRoHistoryItem, lastStoredIn}) => {

            const pickedBy = lastRoHistoryItem?.pickedBy ?? "-";
            const sentToBrief = lastRoHistoryItem?.sentToBrief ?? "-";

            let permRemovalDetails = "";
            let roRemovalDetails = "";
            let lastStoredInDetails = "";
            
            if (lastPermRemoval) {
                permRemovalDetails =
                    `<p class="border-bottom">
                        <strong>Permanently Removed:</strong> 
                                ${LIUtils.shortDateTimeFromJsonUtcDateStr(lastPermRemoval?.date)} by ${lastPermRemoval.actor}
                    </p>`;
            }
            if(pickedBy !== "-" && sentToBrief !== "-") { // some useful retrieval info here
                roRemovalDetails = `<p class="mt-3"><strong>Picked By:</strong> ${pickedBy} on
                                    <u><i>${LIUtils.shortDateTimeFromJsonUtcDateStr(lastRoHistoryItem?.datePickCompleted)}</i></u></p>
                                    <p>
                                    <a href="/retrievals/details/${lastRoHistoryItem?.parentOrderId}">
                                        <strong>Order #:</strong> ${LIUtils.prettyOrderNumber(lastRoHistoryItem?.parentOrderId)}</a><br/>
                                    <strong>Destination: </strong> ${sentToBrief}<br/>
                                    <strong>Date Due Back:</strong> ${LIUtils.shortDateTimeFromJsonUtcDateStr(lastRoHistoryItem?.dateDueBack)}</p>`;
            }
            if(lastStoredIn) {
                const [magBC,magSection,magPosition] = lastStoredIn.split(":");
                const path=`<a href="/mag_details/${magBC}">Mag ${magBC}</a>, Section ${magSection}, Position: ${magPosition}`;
                lastStoredInDetails = `<p>Last stored in ${path}</p>`;
            }

            if (permRemovalDetails==="" && pickedBy === "-" && sentToBrief === "-") { // no useful info on any recent removal or retrieval
                Popup.show("Sample Removal Details", `<p>No recent permanent deletions or retrievals for this sample were found.</p>
                        <p>It is possible that this sample:<ul><li>has never been successfully added into a mag</li><li>or is currently in 'needs attention'</li>
                        <li>or its' mag has been 'Reloaded' and the sample is waiting for reload</li></p>${lastStoredInDetails}</p>`);
                return;
            }
            
            // show some useful info
            Popup.show("Sample Removal Details", `${permRemovalDetails} ${roRemovalDetails} ${lastStoredInDetails}`);
        })
        .catch( responseError => ErrorPopup.showError("ERROR", responseError.error));
    }

    static ShowSampleIsInOutOrPendingOrPicked(sampleItem, handlePendingOrderClicked:function=null, showFullDetails:boolean=false, thisOrderId:number=undefined) {
        if (sampleItem.isDeleted) {
            return <SampleBadgeDeleted/>;
        }
        if(sampleItem.isOffPremises) {
            return <SampleBadgeOffPremises/>;
        }
        let dupBadge = null;
        if(sampleItem.isDuplicate) {
            dupBadge = <SampleBadgeDuplicate/>;
        }
        if (!sampleItem.magBarcode || sampleItem.magBarcode === "") { // OUT
            let pickedBadge=null;
            if(thisOrderId && sampleItem.parentOrderId===Number(thisOrderId)) {
                if(sampleItem.datePickCompleted!=null) { // picked on this order
                    pickedBadge=<SampleBadgePicked/>;
                }
                else { // picked on some other order (or missing!)
                    pickedBadge=<SampleBadgeDifferentOrder/>;
                }
            }
            return (
                <>
                    <SampleBadgeOut onClickHandler={() => ArchiverGuiUtils.GetSampleOutInfo(sampleItem)}/>
                    {pickedBadge}
                    {dupBadge}
                </>
            );
        }

        // sample may be in
        if(sampleItem.isPendingRemovalOnOrder) {
            const pendingOnThisOrder = thisOrderId?sampleItem.isPendingRemovalOnOrder===thisOrderId:false;
            let fullDetails = "(this order)";
            let badgeColor = "badge-info";
            if(!pendingOnThisOrder) {
                fullDetails = "(Order #"+LIUtils.prettyOrderNumber(sampleItem.isPendingRemovalOnOrder)+")";
                badgeColor = "li-bg-warn-light";
            }
            return (
                <>
                    <span className={"badge ml-2 "+badgeColor+" "+(handlePendingOrderClicked?"btn-link li-pointer":"")}
                          onClick={() => handlePendingOrderClicked && handlePendingOrderClicked(sampleItem.isPendingRemovalOnOrder)}>
                        * PENDING {showFullDetails && fullDetails}
                    </span>
                    {dupBadge}
                </>
            );

            //
        }
        return (
            <>
                <SampleBadgeIn/>
                {dupBadge}
            </>
        );
    }
    
    static ShowMagIsPicked(magItem:BTOMagazineItem, handleBadgeClicked:function=null) {
        if (!magItem.datePickCompleted) {
            return null;   
        }
        const pickedBadge = <span className="badge li-bg-picked ml-2 li-fg-white">PICKED</span>;   
        return pickedBadge;
    }
    
    static GetForeignRefBadgeForBox(boxItem:BTOBox, handleBadgeClicked:function=null) {
        if(handleBadgeClicked == null) {
            return <span className="badge li-bg-muted ml-2 li-fg-white"><i className='fa fa-tag'></i></span>;
        }
        let refName = LIUtils.existsAndNotEmpty(boxItem.foreignReferenceName)?boxItem.foreignReferenceName:<i className='fa fa-tag'></i>;
        return <span
            className={"badge li-fg-white ml-2 " + (handleBadgeClicked ? "li-pointer li-bg-box-ref" : "li-bg-muted")}
            onClick={() => handleBadgeClicked && handleBadgeClicked(boxItem, "+ Ref")} // magic string here
            data-toggle="tooltip" data-placement="auto" title="Click to Add Reference">{refName}</span>;
    }
    
    static ShowBoxIsPendingPickedOrShipped(boxItem:BTOBox, 
                       handlePendingOrderClicked:function=null, handleBadgeClicked:function=null,showFullDetails:boolean=false, thisOrderId:number=-1) {
        const refBadge = ArchiverGuiUtils.GetForeignRefBadgeForBox(boxItem, handleBadgeClicked);
        
        // we start out with just the idea that this box is in this order and not yet PICKED
        let pickedBadge = null;
        let pendingBadge = null;
        
        // PENDING
        if(this._isBoxOriginallyFromThisOrderButPendingOnAnotherOrder(boxItem, thisOrderId)) { // on THIS ORDER at the moment
            const orderNumberDetails = "(Order #" + LIUtils.prettyOrderNumber(boxItem.pendingOnOrderId) + ")";
            pendingBadge = <span
                className={"badge ml-2 li-bg-warn-light " + (handlePendingOrderClicked ? "btn-link li-pointer" : "")}
                onClick={() => handlePendingOrderClicked && handlePendingOrderClicked(boxItem.pendingOnOrderId)}>
                    * PENDING {showFullDetails && orderNumberDetails}
                </span>;
        }
        if(this._isBoxOriginallyFromADifferentOrderAndPendingOnThatOrder(boxItem, thisOrderId)) {
            // covers the case where the box has just been added to a new order (not saved) and this box is pending on another
            // order currently
            const orderNumberDetails = "(Order #" + LIUtils.prettyOrderNumber(boxItem.parentOrderId) + ")";
            pendingBadge = <span
                className={"badge ml-2 li-bg-warn-light " + (handlePendingOrderClicked ? "btn-link li-pointer" : "")}
                onClick={() => handlePendingOrderClicked && handlePendingOrderClicked(boxItem.parentOrderId)}>
                    * PENDING {showFullDetails && orderNumberDetails}
                </span>;
        }
        if(this._isBoxOriginallyFromADifferentOrderButPendingOnThisOrder(boxItem, thisOrderId)) { 
            const orderNumberDetails = "(Order #" + LIUtils.prettyOrderNumber(boxItem.parentOrderId) + ")";
            pendingBadge = <span
                className={"badge ml-2 li-bg-warn-light " + (handlePendingOrderClicked ? "btn-link li-pointer" : "")}
                onClick={() => handlePendingOrderClicked && handlePendingOrderClicked(boxItem.parentOrderId)}>
                    * PENDING {showFullDetails && orderNumberDetails}
                </span>;
        }
        
        // PICKED - some of these cases can be clicked to UNPICK
        if(this._isBoxOriginallyFromThisOrderAndPickedOnThisOrder(boxItem, thisOrderId)) {
            // can be unpicked
            pickedBadge = <span className="badge li-bg-picked ml-2 li-fg-white">PICKED</span>;
        }
        if (this._isBoxOriginallyFromThisOrderButPickedOnAnotherOrder(boxItem, thisOrderId) || boxItem.parentOrderId === null) {
            if (boxItem.datePickCompleted != null) { // PICKED  - can be unpicked
                pickedBadge = <span className="badge li-bg-picked-other-order ml-2 li-fg-white">PICKED</span>;
            }
            else { // fall-thru NOT PICKED on this order yet 
                pickedBadge = null;
            }
        }
        if(this._isBoxOriginallyFromADifferentOrderButPickedOnThisOrder(boxItem, thisOrderId)) { 
            const orderNumberDetails = "(Order #" + LIUtils.prettyOrderNumber(boxItem.parentOrderId) + ")";
            pickedBadge = <span
                className={"badge ml-2 li-bg-picked li-fg-white " + (handlePendingOrderClicked ? "btn-link li-pointer" : "")}
                onClick={() => handlePendingOrderClicked && handlePendingOrderClicked(boxItem.parentOrderId)}>
                    PICKED {showFullDetails && orderNumberDetails}
                </span>;
        }
        
        return (
            <>
                {!pickedBadge && pendingBadge}
                {refBadge}
                {pickedBadge}
               
            </>
        );
    }
    
    static _isBoxOriginallyFromThisOrderButPendingOnAnotherOrder(boxItem:BTOBox, thisOrderId:number):boolean {
        return boxItem.parentOrderId===thisOrderId && boxItem.datePickCompleted===null &&boxItem.pendingOnOrderId!=null && boxItem.pendingOnOrderId!==thisOrderId;
    }
    
    static _isBoxOriginallyFromThisOrderAndPickedOnThisOrder(boxItem:BTOBox, thisOrderId:number):boolean {
        return boxItem.parentOrderId===thisOrderId && boxItem.datePickCompleted!=null;
    }
    
    static _isBoxOriginallyFromThisOrderButPickedOnAnotherOrder(boxItem:BTOBox, thisOrderId:number):boolean {
        return boxItem.parentOrderId===thisOrderId && boxItem.datePickCompleted!=null && boxItem.pendingOnOrderId!=null && boxItem.pendingOnOrderId!==thisOrderId;
    }
    
    static _isBoxOriginallyFromADifferentOrderAndPendingOnThatOrder(boxItem:BTOBox, thisOrderId:number):boolean {
        return boxItem.parentOrderId!=null && boxItem.parentOrderId!==thisOrderId && boxItem.pendingOnOrderId===boxItem.parentOrderId;
    }
    
    static _isBoxOriginallyFromADifferentOrderButPendingOnThisOrder(boxItem:BTOBox, thisOrderId:number):boolean {
        return boxItem.parentOrderId!=null && boxItem.parentOrderId!==thisOrderId && boxItem.pendingOnOrderId===thisOrderId;
    }
    
    static _isBoxOriginallyFromADifferentOrderButPickedOnThisOrder(boxItem:BTOBox, thisOrderId:number):boolean {
        return boxItem.parentOrderId!=null && boxItem.parentOrderId!==thisOrderId && boxItem.datePickCompleted!=null && boxItem.pendingOnOrderId===thisOrderId;
    }

    /**
     * get and dump out sample item information related to a sample that is marked as OUT.
     * This is used in several pages, so we share.
     */
    static GetBoxShippedInfo(item) {
        ArchiverClientApi.getBoxLastShippedDetails(item.id)
        .then(({lastBtoHistoryItem, lastStoredIn}) => {

            const pickedBy = lastBtoHistoryItem?.pickedBy ?? "-";
            const sentToBrief = lastBtoHistoryItem?.sentToBrief ?? "-";

            let btoPickedDetails = "";
            let lastStoredInDetails = "";
            
            if(pickedBy !== "-" && sentToBrief !== "-") { // some useful retrieval info here
                btoPickedDetails = `<p class="mt-3"><strong>Picked By:</strong> ${pickedBy} on
                                    <u><i>${LIUtils.shortDateTimeFromJsonUtcDateStr(lastBtoHistoryItem?.datePickCompleted)}</i></u></p>
                                    <p>
                                    <a href="/retrievals/details/${lastBtoHistoryItem?.parentOrderId}">
                                        <strong>Order #:</strong> ${LIUtils.prettyOrderNumber(lastBtoHistoryItem?.parentOrderId)}</a><br/>
                                    <strong>Destination: </strong> ${sentToBrief}<br/>
                                    <strong>Date Due Back:</strong> ${LIUtils.shortDateTimeFromJsonUtcDateStr(lastBtoHistoryItem?.dateDueBack)}</p>`;
            }
            if(lastStoredIn) {
                const [magBC,magSection,magPosition] = lastStoredIn.split(":");
                const path=`<a href="/mag_details/${magBC}">Mag ${magBC}</a>, Section ${magSection}, Position: ${magPosition}`;
                lastStoredInDetails = `<p>Last stored in ${path}</p>`;
            }

            if (pickedBy === "-" && sentToBrief === "-") { // no useful info on any recent removal or retrieval
                Popup.show("Box Removal Details", `<p>No recent transfers for this box were found.</p>
                        <p>It is possible that this box:<ul><li>has never been successfully added into a Transfer Order</li></p>
                        <p>${lastStoredInDetails}</p>`);
                return;
            }

            // show some useful info
            Popup.show("Box Picked Details", `${btoPickedDetails} ${lastStoredInDetails}`);

        })
        .catch( responseError => ErrorPopup.showError("ERROR", responseError.error));
    }

    static ShowSampleIsManuallyEdited(sampleItem) {
        
        if(sampleItem.manuallyAdded===true) {
            return(
                    <span className="badge badge-light li-bg-warn"
                          title="manually edited"><i className="fa fa-pen"/></span>
            );
        }
        
        return null;
    }

    /**
     * show and error popup unless the error is related to being unauthourized since the top level should handle that.
     * @param responseError
     * @param afterFunc
     * @constructor
     */
    static ShowErrorIfNotUnauthourized(responseError, afterFunc:function) {
        
        if (responseError?.statusCode === 401) {
            Popup.show("Unauthorized","<p><b>Your attempt to access the system has failed.</b><br/>This is either because:" +
                "<ul><li>your user code is incorrect</li><li>OR your session has expired</li></ul></p><p>Please dismiss this dialog to login again.</p>",
                () => {
                    window.location.href="/app/signout";
                });
            return;
        }
        let error = responseError?.error??"(none specified)";
        if (typeof error === 'string') {
            const lowerError = error?.toLowerCase() ?? "(none specified)";
            if (lowerError.indexOf("forbidden") !== -1) { // not enough user level
                error = "Forbidden";
            } else if (error.indexOf("trying to proxy") !== -1) {
                error = "<p>Failed to connect to Archiver WebService.</p><p>Please have your Archiver administrator ensure that the Archiver is accessible and that its WebService is running before trying again.</p>";
            }
        }
        ErrorPopup.showError("ERROR", error, afterFunc);
    }

    /**
     * return a single string that is a short description of the storage location
     * @param storageLocation
     * @returns {string}
     * @constructor
     */
    static GetShortLocationStringGivenMagStorageLocation(storageLocation:DepartmentWithLocation) {
        const deptName = storageLocation?.department?.name;
        const locationName = storageLocation?.location?.name;

        if(!deptName && !locationName) {
            return "(no location specified)";
        }
        if(!locationName) {
            return `??/${deptName}`;
        }
        if(!deptName) {
            return `${locationName}`;
        }
        return `${locationName}/${deptName}`;
    }

    /** search the provided list of locations for a department id and return a pretty location string
     * 
     * @param deptId
     * @param fromLocations
     * @returns {string}
     * @constructor
     */
    static GetLocationBasedOnDeptId(deptId: number, fromLocations: []):string {
        if (!deptId || deptId < 1) {
            return DEFAULT_LOCATION_NAME;
        }
        for (let i = 0; i < fromLocations.length; i++) {
            const loc = fromLocations[i];
            const dept = loc.departments?.find(dept => dept.id === deptId);
            if (dept) {
                return loc.name + "/"+ dept.name;
            }
            // keep searching...
        }
        return DEFAULT_LOCATION_NAME;
    }
}

export default ArchiverGuiUtils;